import { useCallback } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import {
  accountEmailExists as accountEmailExistsQuery,
  MeQuery,
} from '../../../../graphql/queries/account';
import useAuthenticated from '../../../../hooks/__mocks__/useAuthenticated';
import { isProAccount } from '../../../../util/account';
import ACCOUNT_ROLES from '../../../../constants/account-roles';
import {
  accountAddRoles as accountAddRolesMutation,
  accountLogin as accountLoginMutation,
  accountUpdate as accountUpdateMutation,
  clientStoreSet as clientStoreSetMutation,
} from '../../../../graphql/mutations/account';
import { loginSuccess } from '../../../../actions/auth-actions';

const useLoginAuth = () => {
  const authenticated = useAuthenticated();
  const client = useApolloClient();
  const dispatch = useDispatch();
  const onLoginSuccess = useCallback(() => dispatch(loginSuccess()), [
    dispatch,
  ]);
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });

  const [accountLogin] = useMutation(accountLoginMutation);
  const [accountUpdate] = useMutation(accountUpdateMutation);
  const [accountAddRoles] = useMutation(accountAddRolesMutation);
  const [clientStoreSet] = useMutation(clientStoreSetMutation);

  const emailExists = async emailAddress => {
    const res = await client.query({
      query: accountEmailExistsQuery,
      variables: {
        email_address: emailAddress,
      },
    });

    return res.data.accountEmailExists.exists;
  };

  const onAccountLogin = async (email, password) => {
    const result = await accountLogin({
      variables: {
        input: { email, password },
      },
    });

    return result;
  };

  const onAccountUpdate = async input => {
    const result = accountUpdate({
      variables: {
        input,
      },
    });

    return result;
  };

  const onAddShutterbugRole = async accountId => {
    const result = await accountAddRoles({
      variables: {
        input: {
          account_id: accountId,
          // note: ACCOUNT_ROLES enum contains lowercase, but API requires uppercase
          roles: [ACCOUNT_ROLES.SHUTTERBUG.toUpperCase()],
        },
      },
    });

    return result;
  };

  const onClientStoreSet = async input => {
    const result = await clientStoreSet({
      variables: {
        input,
      },
    });

    return result;
  };

  const onAttachRolesToAccount = async () => {
    const shouldAddShutterbugRole = !isProAccount(user);

    if (
      shouldAddShutterbugRole &&
      !user.roles.includes(ACCOUNT_ROLES.SHUTTERBUG)
    ) {
      // Set the time the user signed up, and forget (no need to await).
      onClientStoreSet({
        account_id: user.id,
        key: 'shutterbug_signup_timestamp',
        data: moment().valueOf().toString(),
      });

      await onAddShutterbugRole(user.id);
    }
  };

  return {
    user,
    emailExists,
    onAccountLogin,
    onAccountUpdate,
    onLoginSuccess,
    onAttachRolesToAccount,
  };
};

export default useLoginAuth;
